import React, { useContext, useEffect } from 'react';
import { Button, Box, Typography, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import styles from './styles.module.css';
import { useHistory } from 'react-router-dom';
import { ROUTES, REGISTRATION_TYPE } from 'const';
import { OnlinePurchaseContext } from 'Context/OnlinePurchaseContext';
import { calculatePayableAmount } from 'helpers/SubscriptionHelper';
import { ADDITIONAL_FEATURES_LEFT, ADDITIONAL_FEATURES_RIGHT } from 'components/pages/Register/RegisterConfig';

const PLAN_MAPPING = {
  'Sanoft Lite': 'sanoft-lite',
  'Sanoft Pro': 'sanoft-pro',
  'Sanoft Elite': 'sanoft-elite',
};

const PLAN_DATA = [
  {
    title: 'Sanoft Lite',
    subtitle: 'Perfect start for small businesses',
    price: '5000',
    features: [
      'POS Billing & Sales Operations',
      'Sales, Purchase & Inventory Reports',
      'Customer, Purchase, Tax, Stock, Shift and Party Reports',
      'Purchase & Purchase Return',
      'Vendor Management',
      'Customer Management',
      'Customer Group Management (Retail / Wholesale)',
      'Accounts (Heads & Ledgers)',
      'Online Order Management',
      'Stock Transfer',
      'Product & Category Management',
      'Salesperson Management',
      'Multi-Payment Support',
      'Offline Mode with Auto Sync',
      'KOT (Kitchen Order Ticket)',
    ],
  },
  {
    title: 'Sanoft Pro',
    subtitle: 'Advanced features for growing businesses',
    price: '7000',
    highlight: true,
    badge: '★ Best value',
    features: [
      'All Lite Features',
      'Profit Reports',
      'Expense Management',
      'Production Management',
      'Price Categories (Set different prices for AC and Non-AC seating)',
      'Product Options / Variants',
      'Product Batch Management',
      'Product Scheduling',
      'Category Scheduling',
      'Feedback Management',
    ],
  },
  {
    title: 'Sanoft Elite',
    subtitle: 'Take your operations to the next level',
    price: '9000',
    features: [
      'All Lite + Pro Features',
      'Unlimited Captain Devices (Waiter Order Taking)',
      'Kitchen Staff Role Assignment',
      'Area Management',
      'Table Management',
      'Third-Party Software Integrations',
    ],
  },
];

export default function PlanPrice({ open, onClose }) {
  const history = useHistory();
  const { setPurchaseData, subscriptionsAndAddons, loadSubscriptionsAndAddons } = useContext(OnlinePurchaseContext);
  useEffect(() => {
    if (open) {
      loadSubscriptionsAndAddons();
    }
  }, [open, loadSubscriptionsAndAddons]);

  const handleBuyButton = async (plan) => {
    const subKey = PLAN_MAPPING[plan.title];
    const sub = (subscriptionsAndAddons?.subscriptions || []).find((s) => s.key === subKey);

    if (!sub) return;

    const amount = sub.yearly_price;

    const { tax, payable } = calculatePayableAmount(amount, sub?.country);

    const data = {
      subscription: {
        key: sub.key,
        id: sub.id,
        validity: 365,
        name: sub.name,
        price: amount,
      },
      add_ons: [],
      amount,
      tax,
      payable,
      action_type: REGISTRATION_TYPE.RENEWAL,
    };

    setPurchaseData(data);
    history.push(ROUTES.PROCESS_PAYMENT);
  };
  if (!open) return null;

  return (
    <div className={styles.popUp}>
      <div className={`${styles.contentWrapper} ${styles.planPriceWrapper}`}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>Choose Your Plan</h2>
          <CloseIcon className={styles.cursorPointer} onClick={onClose} />
        </div>

        <div className={styles.inputSec}>
          <Box className={styles.pricingContainer}>
            {PLAN_DATA.map((plan, index) => (
              <Box key={index} className={`${styles.card} ${plan.highlight ? styles.highlight : ''}`}>
                {plan.badge && <div className={styles.badge}>{plan.badge}</div>}
                <Typography variant="h6" component="h3">
                  {plan.title}
                </Typography>
                <Typography className={styles.subtitle}>{plan.subtitle}</Typography>
                <Typography variant="h3" component="h1" className={styles.planPrice}>
                  ₹{plan.price}
                  <span>/Year</span>
                </Typography>

                <Button variant="contained" className={styles.button} onClick={() => handleBuyButton(plan)}>
                  Upgrade Now
                </Button>

                <ul>
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex}>
                      <RadioButtonCheckedIcon className={styles.targetIcon} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </Box>
            ))}
          </Box>
          <Box sx={{ marginTop: 3 }}>
            <div className={styles.additionalFeaturesCard}>
              <div className={styles.additionalHeaderBanner}>
                ADDITIONAL FEATURES
              </div>
              <div className={styles.additionalBody}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {ADDITIONAL_FEATURES_LEFT.map((feat) => (
                        <li key={feat} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', fontSize: '13.5px', fontWeight: 700, color: '#0f172a' }}>
                          <RadioButtonCheckedIcon className={styles.targetIcon} />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {ADDITIONAL_FEATURES_RIGHT.map((feat) => (
                        <li key={feat} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', fontSize: '13.5px', fontWeight: 700, color: '#0f172a' }}>
                          <RadioButtonCheckedIcon className={styles.targetIcon} />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </Grid>
                </Grid>
              </div>
            </div>
          </Box>
        </div>

        <div className={`${styles.footSec} ${styles.planPriceFootSec}`}>
          <Box className={styles.footerLinks}>
            <a href="https://sanoft.com/terms" target="_blank" rel="noreferrer" className={styles.footerLink}>
              Terms of Service
            </a>
            <span className={styles.dividerDot}>•</span>
            <a href="https://sanoft.com/privacy" target="_blank" rel="noreferrer" className={styles.footerLink}>
              Privacy Policy
            </a>
          </Box>
        </div>
      </div>
    </div>
  );
}
